import React, { FC, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { organizationAtom, signedInUserAtom } from "../state/atoms";
import { AppUser } from "../types/users";
import { Organization } from "../types/organizations";
import { isProduction } from "../services/config";
import { readCrmPreview } from "../components/CrmUi/useCrm";
import { DEV_PREVIEW_LICENSED_EMAILS } from "../services/devPreview";
import Dashboard from "./Dashboard";

/**
 * Development and staging only: the dashboard for a made-up signed-in user,
 * so its CRM looks can be compared and screenshotted without an account.
 * Open /dev/crm-preview?crmTheme=hubspot or ?crmTheme=outreach. Nothing here
 * is real: the stats, reports and users the tabs load come from
 * services/devPreview.ts, and any other backend call fails as signed out.
 */
const DevCrmPreview: FC = () => {
  const { search } = useLocation();
  const crm = readCrmPreview(search) ?? "outreach";
  const [user, setUser] = useRecoilState(signedInUserAtom);
  const [organization, setOrganization] = useRecoilState(organizationAtom);
  const hubspot = crm === "hubspot";
  // ?role=user previews what a regular (non-admin) user sees.
  const profile = new URLSearchParams(search).get("role") === "user" ? "DEFAULT" : "ADMIN";

  useEffect(() => {
    if (isProduction) return;
    if (!user || user.uid !== "dev-crm-preview" || user.profile !== profile) {
      setUser({
        uid: "dev-crm-preview",
        profile,
        email: "preview@example.com",
        firstName: "Preview",
        organizationId: "dev-crm-preview-org",
        organizationName: "Acme Preview",
        isOutreachAuthorized: !hubspot,
        isHubSpotAuthorized: hubspot,
        isProspectWebhookCreated: true,
        isPayingCustomer: true,
        isSegmentedAccount: true,
      } as unknown as AppUser);
    }
    if (!organization || organization.id !== "dev-crm-preview-org" || organization.initialIntegrationType !== crm) {
      setOrganization({
        id: "dev-crm-preview-org",
        name: "Acme Preview",
        initialIntegrationType: crm,
        isOutreachAuthorized: !hubspot,
        isHubSpotAuthorized: hubspot,
        isPayingCustomer: true,
        isEnrichmentEnabled: true,
        availableLicenses: 10,
        allowedOwnerEmails: DEV_PREVIEW_LICENSED_EMAILS,
      } as unknown as Organization);
    }
  }, [user, organization, crm, hubspot, profile, setUser, setOrganization]);

  if (isProduction) return <Navigate to="/" replace />;
  if (!user || !organization) return null;
  return <Dashboard />;
};

export default DevCrmPreview;
